import React, { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { HelpLink } from '@/features/help';
import { TourCardChrome } from './TourCardChrome';
import { TourMedia } from './TourMedia';
import type { ResolvedFrame, TourMedia as TourMediaType, TourStep } from '../types';

interface Props {
  step: TourStep;
  frame: ResolvedFrame;
  /** Resolved media for the current (step, frame) — see `resolveMedia` in types.ts. */
  media: TourMediaType;
  /** Resolved media for the next step (or next frame) — used for lazy preload. */
  preloadNext?: TourMediaType;
  stepNumber: number;
  totalSteps: number;
  frameNumber: number;
  frameCount: number;
  targetEl: HTMLElement | null;
  secondaryEl?: HTMLElement | null;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  onRemindLater?: () => void;
  hideRemindLater?: boolean;
  /**
   * Jump-to-frame callback wired by the TourProvider. When omitted (e.g.
   * unit tests), the dots fall back to inert spans so the indicator is
   * still visually present but not interactive.
   */
  onGoToFrame?: (targetIndex: number) => void;
}

/**
 * Compact tour card body — a 400px-wide anchored popover with title /
 * body / typed media slot / docs link / frame dots stacked vertically.
 *
 * In Phase 1 this is the only layout; Phase 2 adds `<TourCardSplit />`
 * and TourCard becomes a layout dispatcher (Task 5).
 */
export const TourCardCompact: React.FC<Props> = ({
  step,
  frame,
  media,
  preloadNext,
  stepNumber,
  totalSteps,
  frameNumber,
  frameCount,
  targetEl,
  secondaryEl,
  isFirst,
  isLast,
  onNext,
  onBack,
  onSkip,
  onRemindLater,
  hideRemindLater,
  onGoToFrame,
}) => {
  const ctaRef = useRef<HTMLButtonElement | null>(null);

  // Re-focus the primary CTA on every step or frame transition. The chrome
  // also focuses on mount; this picks up subsequent transitions while the
  // card stays mounted.
  useEffect(() => {
    ctaRef.current?.focus();
  }, [step.id, frame.title, frame.body]);

  return (
    <TourCardChrome
      titleId={`tour-step-title-${step.id}`}
      bodyId={`tour-step-body-${step.id}`}
      stepNumber={stepNumber}
      totalSteps={totalSteps}
      targetEl={targetEl}
      secondaryEl={secondaryEl}
      placement={frame.placement}
      isFirst={isFirst}
      isLast={isLast}
      onNext={onNext}
      onBack={onBack}
      onSkip={onSkip}
      onRemindLater={onRemindLater}
      hideRemindLater={hideRemindLater}
      widthClassName={frame.cardWidthClass ?? 'w-[400px]'}
      primaryCtaRef={ctaRef}
    >
      {/* Vertical rhythm uses a single `mt-3` between content blocks so the
          compact card reads as "calm stack" — title, body, media, docs link,
          frame dots — rather than a jagged list with mixed gaps. */}
      <h2
        id={`tour-step-title-${step.id}`}
        className="mt-3 text-base font-semibold text-gray-900"
        aria-live="polite"
      >
        {frame.title}
      </h2>
      <p
        id={`tour-step-body-${step.id}`}
        className="mt-2 text-sm leading-5 text-gray-600"
        aria-live="polite"
      >
        {frame.body}
      </p>

      {media.type !== 'none' && (
        <div className="mt-3">
          <TourMedia media={media} preloadNext={preloadNext} />
        </div>
      )}

      {frame.docsLink && (
        <div className="mt-3">
          <HelpLink
            to={frame.docsLink}
            label="Learn more"
            className="text-primary-700 hover:text-primary-900"
          />
        </div>
      )}

      {frameCount > 1 && (
        <div
          className="mt-3 flex items-center justify-center gap-1.5"
          role="group"
          aria-label={`Frame ${frameNumber} of ${frameCount}`}
        >
          {Array.from({ length: frameCount }).map((_, i) =>
            onGoToFrame ? (
              <button
                key={i}
                type="button"
                onClick={() => onGoToFrame(i)}
                aria-current={i === frameNumber - 1 ? 'step' : undefined}
                aria-label={`Go to frame ${i + 1} of ${frameCount}`}
                className={clsx(
                  'h-2 w-2 rounded-full transition-colors',
                  i === frameNumber - 1
                    ? 'bg-primary-600'
                    : 'bg-gray-300 hover:bg-gray-400',
                )}
              />
            ) : (
              // Inert variant uses the same 8px footprint so the visual
              // rhythm of the row doesn't shift if the consumer omits
              // onGoToFrame (mostly tests).
              <span
                key={i}
                aria-current={i === frameNumber - 1 ? 'step' : undefined}
                className={clsx(
                  'h-2 w-2 rounded-full transition-colors',
                  i === frameNumber - 1 ? 'bg-primary-600' : 'bg-gray-300',
                )}
              />
            ),
          )}
        </div>
      )}
    </TourCardChrome>
  );
};
